import { zodResolver } from '@hookform/resolvers/zod';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { useEffect } from 'react';
import { FormProvider } from 'react-hook-form';
import { FormLayout, FormRow, FormSection, LoadingSpinner, Select, TextInput } from '~/components';
import { useCustomForm } from '~/hooks/useCustomForm';
import { InfoMessage } from '../../../components/InfoMessage';
import { useJobListingActorRef } from '../../jobListingProvider';
import { COMPENSATION_TYPE_OPTIONS, emptyCompensationForm, RVU_PERIOD_OPTIONS } from './constants';
import { CompensationFormSchema, CompensationFormValues } from './types';
import { transformApiToCompensationForm } from './utils';

const TAGS = CT.employer.jobListings.compensationForm;

export const CompensationForm = ({ footer }: { footer: React.ReactNode }) => {
  const jobListingRef = useJobListingActorRef();
  const { compensationStepRef, listing } = useSelector(jobListingRef, (state) => state.context);

  const methods = useCustomForm<CompensationFormValues>({
    // @ts-expect-error typing for preprocess parsing does not work well
    resolver: zodResolver(CompensationFormSchema),
    defaultValues: listing?.detail?.id
      ? transformApiToCompensationForm(listing)
      : emptyCompensationForm,
  });

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isDirty },
    unregister,
  } = methods;

  useEffect(() => {
    if (listing) {
      setValue('jobListingId', listing.id);
      setValue('id', listing.detail?.id);
      setValue('laborReqType', listing.detail?.laborReqType ?? null);
    }
  }, [listing]);

  const onSubmitSuccess = (values: CompensationFormValues) => {
    if (isDirty) {
      compensationStepRef.send({ type: 'ON_UPDATE', payload: { values } });
    } else {
      jobListingRef.send({ type: 'ON_NEXT' });
    }
  };

  const onSubmitError = (err: unknown) => {
    console.log('Compensation Form Submit Error: ', err, methods.getValues());
  };

  const compType = watch('compType');
  const isFteRvu = compType.value === 'RVU' && listing?.detail?.laborReqType === 'FTE';

  const fieldToUnregister: (keyof CompensationFormValues)[] = [
    'pricePerHour',
    'pricePerShift',
    'pricePerRvu',
    'rvusPerPeriod',
    'rvuPeriod',
  ];

  const compTypeToFields: Record<
    CompensationFormValues['compType']['value'],
    keyof CompensationFormValues
  > = {
    HOUR: 'pricePerHour',
    SHIFT: 'pricePerShift',
    RVU: 'pricePerRvu',
  };

  useEffect(() => {
    fieldToUnregister.forEach((field) => {
      if (field === compTypeToFields[compType.value]) return;
      if ((field === 'rvusPerPeriod' || field === 'rvuPeriod') && isFteRvu) return;
      unregister(field);
      setValue(field, null);
    });
  }, [compType, isFteRvu]);

  if (!listing?.detail?.id) {
    return <LoadingSpinner />;
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmitSuccess, onSubmitError)}>
        <FormLayout>
          <FormSection componentsPerLine={2}>
            <FormRow>
              <Select
                name={'compType'}
                cyTag={TAGS.compType}
                label={'Compensation Type'}
                options={COMPENSATION_TYPE_OPTIONS}
                required
              />
            </FormRow>
          </FormSection>
          <FormSection componentsPerLine={2}>
            <FormRow>
              {compType.value === 'HOUR' && (
                <TextInput
                  name={'pricePerHour'}
                  cyTag={TAGS.pricePerHour}
                  label={'Price per Hour'}
                  type="number"
                  required
                />
              )}
              {compType.value === 'SHIFT' && (
                <TextInput
                  name={'pricePerShift'}
                  cyTag={TAGS.pricePerShift}
                  label={'Price per Shift'}
                  type="number"
                  required
                />
              )}
              {compType.value === 'RVU' && (
                <TextInput
                  name={'pricePerRvu'}
                  cyTag={TAGS.pricePerRvu}
                  label={'Price per RVU'}
                  type="number"
                  required
                />
              )}
            </FormRow>
            {isFteRvu && (
              <FormRow>
                <TextInput
                  name={'rvusPerPeriod'}
                  cyTag={TAGS.rvusPerPeriod}
                  label={'Expected RVUs'}
                  description="How many RVUs this role is expected to generate."
                  type="number"
                  required
                />
              </FormRow>
            )}
          </FormSection>
          {isFteRvu && (
            <FormSection componentsPerLine={2}>
              <FormRow>
                <Select
                  name={'rvuPeriod'}
                  cyTag={TAGS.rvuPeriod}
                  label={'Counted per'}
                  description="Used with the RVU price to work out hourly and per-shift rates."
                  options={RVU_PERIOD_OPTIONS}
                  required
                />
              </FormRow>
            </FormSection>
          )}
          <InfoMessage
            withOwnMargin={false}
            message={
              'Note: Full compensation calculations including shift differentials will be shown in Step 6: Cost Calculator'
            }
          />
          {footer}
        </FormLayout>
      </form>
    </FormProvider>
  );
};
